/* ==========================================================================
   FOOTBALL-THEME :: Motion physics
   Tiny, dependency-free helpers behind the Apple-style motion pass
   (WWDC "Designing Fluid Interfaces", applied to the web):

   - spring()      interruptible, velocity-aware spring using Apple's
                   designer parameters: damping ratio + response. Can be
                   retargeted mid-flight; motion continues from the current
                   value and velocity, so reversals have no seam.
   - tracker()     pointer velocity from the last ~120ms of movement (px/s)
   - project()     momentum projection - where a flick is heading
   - rubberband()  progressive resistance past a boundary
   - translate()   reads an element's live translate on an axis, i.e. the
                   presentation value an interruption must start from

   Loaded from layout/theme.liquid <head> (deferred) before every section
   script that uses it: slideshow, testimonials, reviews, filter dock, nav.
   ========================================================================== */
(function () {
  'use strict';

  if (window.fsPhysics) return;

  var reduceQuery =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function reduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  /* Interruptible spring. Call retarget() at any moment - even mid-flight -
     and the motion continues from the current value and velocity.
     damping: 1.0 = critically damped (no overshoot); ~0.8 = slight bounce,
     reserved for momentum-driven settles. response: seconds to reach the
     target (not a duration). */
  function spring(opts) {
    opts = opts || {};

    var state = {
      value: opts.from || 0,
      velocity: opts.velocity || 0,
      target: opts.to == null ? opts.from || 0 : opts.to,
      damping: opts.damping == null ? 1 : opts.damping,
      response: opts.response == null ? 0.4 : opts.response,
      epsilon: opts.epsilon == null ? 0.1 : opts.epsilon,
      running: false,
      raf: 0,
      last: 0,
      onUpdate: opts.onUpdate || null,
      onComplete: null
    };

    function step(now) {
      if (!state.running) return;

      var dt = state.last ? Math.min((now - state.last) / 1000, 0.064) : 1 / 60;
      state.last = now;

      var w = (2 * Math.PI) / Math.max(0.05, state.response);
      var x = state.value - state.target;
      var v = state.velocity;

      /* Integrate in small substeps. At 60fps a single big step is stable,
         but a slow frame (heavy page, first paint) can push the explicit
         damping term past 2 and make the spring run away. Substepping keeps
         it stable at any frame time and is cheap (a handful of adds). */
      var steps = Math.max(1, Math.ceil(dt / (1 / 240)));
      var h = dt / steps;
      for (var i = 0; i < steps; i++) {
        v += (-w * w * x - 2 * state.damping * w * v) * h;
        x += v * h;
      }

      if (!isFinite(x) || !isFinite(v)) {
        x = 0;
        v = 0;
      }

      state.value = state.target + x;
      state.velocity = v;

      if (state.onUpdate) state.onUpdate(state.value, state.velocity);

      if (Math.abs(x) < state.epsilon && Math.abs(v) < state.epsilon * 20) {
        state.value = state.target;
        state.velocity = 0;
        state.running = false;
        state.raf = 0;
        if (state.onUpdate) state.onUpdate(state.value, 0);
        var done = state.onComplete;
        state.onComplete = null;
        if (done) done();
        return;
      }

      state.raf = window.requestAnimationFrame(step);
    }

    var api = {
      get value() {
        return state.value;
      },
      get velocity() {
        return state.velocity;
      },
      get target() {
        return state.target;
      },
      get running() {
        return state.running;
      },
      start: function () {
        if (state.running) return;
        state.running = true;
        state.last = 0;
        state.raf = window.requestAnimationFrame(step);
      },
      stop: function () {
        state.running = false;
        state.onComplete = null;
        if (state.raf) window.cancelAnimationFrame(state.raf);
        state.raf = 0;
      },
      /* New target; keeps value + velocity so a reversal has no seam. */
      retarget: function (to, velocity, damping, response, onComplete) {
        state.target = to;
        if (velocity != null) state.velocity = velocity;
        if (damping != null) state.damping = damping;
        if (response != null) state.response = response;
        state.onComplete = onComplete || null;
        api.start();
      },
      /* Hard set: reduced motion settles, clone wraps, resize. */
      jump: function (value) {
        api.stop();
        state.value = value;
        state.velocity = 0;
        state.target = value;
        if (state.onUpdate) state.onUpdate(value, 0);
      }
    };

    return api;
  }

  /* Pointer velocity: keeps the last ~120ms of samples, returns px/s. */
  function tracker() {
    var samples = [];

    return {
      add: function (pos, time) {
        samples.push({ p: pos, t: time });
        var cutoff = time - 120;
        while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
        if (samples.length > 8) samples.shift();
      },
      /* Pass the release time to get 0 when the pointer paused (>120ms):
         a still finger has no momentum. */
      velocity: function (now) {
        if (samples.length < 2) return 0;
        var b = samples[samples.length - 1];
        if (now != null && now - b.t > 120) return 0;
        var a = samples[0];
        var dt = (b.t - a.t) / 1000;
        if (dt <= 0.001) return 0;
        var v = (b.p - a.p) / dt;
        return Math.max(-9000, Math.min(9000, v));
      },
      reset: function () {
        samples = [];
      }
    };
  }

  /* Apple's exponential-decay projection: the resting point of a flick.
     decelerationRate ~0.998 = normal scroll feel, 0.99 = snappier. */
  function project(velocity, decelerationRate) {
    var d = decelerationRate == null ? 0.998 : decelerationRate;
    return (velocity / 1000) * (d / (1 - d));
  }

  /* Progressive resistance past a boundary (constant 0.55 = Apple's sample);
     the further past the bound, the less the element follows. */
  function rubberband(overshoot, dimension, constant) {
    var c = constant == null ? 0.55 : constant;
    var size = dimension || 1;
    return (overshoot * size * c) / (size + c * Math.abs(overshoot));
  }

  /* Live translate value on an axis - the presentation value to start from. */
  function translate(el, axis) {
    if (!el) return 0;
    var transform = window.getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    var match = transform.match(/\((.+)\)/);
    if (!match) return 0;
    var values = match[1].split(', ');
    var value =
      values.length > 12
        ? values[12 + (axis === 'y' ? 1 : 0)] /* matrix3d */
        : values[axis === 'y' ? 5 : 4]; /* matrix */
    return parseFloat(value) || 0;
  }

  window.fsPhysics = {
    spring: spring,
    tracker: tracker,
    project: project,
    rubberband: rubberband,
    translate: translate,
    reduced: reduced,
    clamp: function (value, min, max) {
      return Math.min(max, Math.max(min, value));
    }
  };
})();
